/**
 * Cart drawer.
 *
 * Intercepts product form submits, posts to Shopify's AJAX cart API, then
 * re-renders the drawer through the Section Rendering API. Only the contents and
 * footer are swapped so the panel keeps its open state and focus position.
 *
 * Falls back to a normal page POST whenever the drawer is disabled in theme
 * settings or a request fails, so adding to cart never silently breaks.
 */
(function () {
  var SECTION_ID = 'cart-drawer';

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function isEnabled() {
    var d = drawer();
    return !!d && d.getAttribute('data-enabled') === 'true';
  }

  var lastFocus = null;

  function open() {
    var d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    // Next frame, so the transition runs rather than snapping.
    requestAnimationFrame(function () {
      d.classList.add('is-open');
    });
    document.body.style.overflow = 'hidden';
    var close = d.querySelector('[data-cart-close]');
    if (close && close.focus) close.focus();
    startTimer();
  }

  function close() {
    var d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    document.body.style.overflow = '';
    stopTimer();
    window.setTimeout(function () {
      if (!d.classList.contains('is-open')) d.hidden = true;
    }, 300);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ── Hold timer ─────────────────────────────────────────────────────────── */
  var timerId = null;

  function stopTimer() {
    if (timerId) { window.clearInterval(timerId); timerId = null; }
  }

  function startTimer() {
    stopTimer();
    var el = document.querySelector('[data-cart-timer]');
    if (!el) return;
    var out = el.querySelector('[data-cart-timer-value]');
    if (!out) return;
    var remaining = (parseInt(el.getAttribute('data-minutes'), 10) || 10) * 60;
    function tick() {
      var m = Math.floor(remaining / 60);
      var sec = remaining % 60;
      out.textContent = m + ':' + (sec < 10 ? '0' : '') + sec;
      if (remaining <= 0) { stopTimer(); return; }
      remaining -= 1;
    }
    tick();
    timerId = window.setInterval(tick, 1000);
  }

  /* ── Rendering ──────────────────────────────────────────────────────────── */
  function refresh() {
    return fetch(window.location.pathname + '?sections=' + SECTION_ID, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var d = drawer();
        if (!d) return;

        var newBody = doc.querySelector('[data-cart-drawer-contents]');
        var oldBody = d.querySelector('[data-cart-drawer-contents]');
        if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

        var newFooter = doc.querySelector('[data-cart-drawer-footer]');
        var oldFooter = d.querySelector('[data-cart-drawer-footer]');
        var panel = d.querySelector('.cart-drawer__panel');
        if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
        else if (newFooter && panel) panel.appendChild(newFooter);
        else if (!newFooter && oldFooter) oldFooter.remove();

        var newCount = doc.querySelector('[data-cart-drawer-count]');
        var oldCount = d.querySelector('[data-cart-drawer-count]');
        if (newCount && oldCount) oldCount.textContent = newCount.textContent;

        syncHeaderCount();
        startTimer();
      })
      .catch(function () { /* leave the drawer as-is; the cart page still works */ });
  }

  function syncHeaderCount() {
    return fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  /* ── Cart mutations ─────────────────────────────────────────────────────── */
  function changeLine(line, quantity) {
    return fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(refresh);
  }

  /* ── Wiring ─────────────────────────────────────────────────────────────── */
  function init() {
    var d = drawer();
    if (!d || d.dataset.cartReady) return;
    d.dataset.cartReady = '1';

    // Open / close
    document.addEventListener('click', function (e) {
      var closer = e.target.closest('[data-cart-close]');
      if (closer) { e.preventDefault(); close(); return; }

      var opener = e.target.closest('[data-cart-open], .header__cart-icon');
      if (opener && isEnabled()) { e.preventDefault(); open(); refresh(); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && d.classList.contains('is-open')) close();
    });

    // Quantity steppers, remove, and manual quantity entry
    d.addEventListener('click', function (e) {
      var step = e.target.closest('[data-cart-qty]');
      if (step) {
        var line = parseInt(step.getAttribute('data-cart-qty'), 10);
        var delta = parseInt(step.getAttribute('data-delta'), 10);
        var input = d.querySelector('[data-cart-qty-input="' + line + '"]');
        var next = Math.max(0, (parseInt(input && input.value, 10) || 0) + delta);
        changeLine(line, next);
        return;
      }
      var remove = e.target.closest('[data-cart-remove]');
      if (remove) changeLine(parseInt(remove.getAttribute('data-cart-remove'), 10), 0);
    });

    d.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      var line = parseInt(input.getAttribute('data-cart-qty-input'), 10);
      changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
    });

    // Intercept add-to-cart so the drawer opens instead of navigating.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isEnabled()) return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      e.preventDefault();
      var button = form.querySelector('[data-add-to-cart-button]');
      if (button) button.setAttribute('aria-busy', 'true');

      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          open();
          return refresh();
        })
        .catch(function () {
          // Anything unexpected: fall back to the normal page POST.
          form.submit();
        })
        .finally(function () {
          if (button) button.removeAttribute('aria-busy');
        });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
